import {Component, computed, inject, Input, OnInit, signal} from '@angular/core';
import {AgGridAngular} from "ag-grid-angular";
import {DatePipe, NgClass} from "@angular/common";
import {ContainerComponent} from "@coreui/angular";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatDialog} from "@angular/material/dialog";
import {ColDef, GridApi, GridReadyEvent, SizeColumnsToFitGridStrategy} from "ag-grid-community";
import {FaqScopeTypePipe} from "../../../pipes/faq-scope-type.pipe";
import {FrequentlyAskedQuestionsService} from "../../../services/frequently-asked-questions.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {IFrequentlyAskedQuestions} from "../../../interfaces/IFrequentlyAskedQuestions";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {FormatBoolean} from "../../../components/cell-renderers/show-played/format-boolean.component";
import {FaqAddComponent} from "../../../components/frequently-asked-questions/faq-add/faq-add.component";
import {FaqManageActionsComponent} from "../../../components/frequently-asked-questions/faq-manage-actions/faq-manage-actions.component";
import {ChooseYourGameComponent} from "../../../components/choose-your-game/choose-your-game.component";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {FaqScopeTypes} from "../../../enum/FaqScopeTypes";
import {FaqFilterType} from "../../../enum/FaqFilterType";

@Component({
  selector: 'app-manage-game-faqs',
  standalone: true,
  imports: [
    AgGridAngular,
    NgClass,
    ContainerComponent,
    MatButton,
    MatIcon,
    MatTooltip,
    MatProgressSpinner,
    ChooseYourGameComponent,
  ],
  templateUrl: './manage-game-faqs.component.html',
  styleUrl: './manage-game-faqs.component.css'
})
export class ManageGameFaqsComponent implements OnInit {
  @Input() canEditWebappFaqs!: boolean;
  @Input() canEditAdminAppFaqs!: boolean;

  private datePipe = inject(DatePipe);
  private faqScopeTypePipe = inject(FaqScopeTypePipe);
  private frequentlyAskedQuestionsService = inject(FrequentlyAskedQuestionsService);
  private errorHandlingService = inject(ErrorHandlingService);
  private matDialog = inject(MatDialog);

  protected readonly FaqFilterType = FaqFilterType;
  protected readonly FaqScopeTypes = FaqScopeTypes;

  protected selectedAppType = signal<FaqScopeTypes | null>(null);
  protected selectedGame = signal<IGameQueryResult | null>(null);
  protected currentFilter = signal<FaqFilterType>(FaqFilterType.GameSpecific);
  protected allFaqs = signal<IFrequentlyAskedQuestions[]>([]);

  protected filteredFaqs = computed(() => {
    const faqs = this.allFaqs();
    const selectedGameId = this.selectedGame()?.Id;
    const appType = this.selectedAppType();

    if (!appType) return [];

    let filtered = faqs.filter(faq => faq.ScopeType === appType);

    filtered = filtered.filter(faq => {
      if (faq.ScopeType === FaqScopeTypes.WebApp && this.canEditWebappFaqs) return true;
      return faq.ScopeType === FaqScopeTypes.AdminApp && this.canEditAdminAppFaqs;
    });

    const filterType = this.currentFilter();
    if (filterType === FaqFilterType.Shared) {
      filtered = filtered.filter(faq => faq.Shared);
    } else if (filterType === FaqFilterType.GameSpecific) {
      filtered = filtered.filter(faq => {
        if (faq.Shared) return false;
        if (!selectedGameId) return true;
        return faq.GameIds && faq.GameIds.includes(selectedGameId);
      });
    }

    return filtered;
  });

  public autoSizeStrategy: SizeColumnsToFitGridStrategy = {type: 'fitGridWidth'};
  public defaultColDef: ColDef = {flex: 1, cellDataType: false};
  public uiState = UIStateEnum.ShowLoading;
  public UIStateEnum = UIStateEnum;
  public faqsGridApi!: GridApi<IFrequentlyAskedQuestions>;

  public faqDefs: ColDef[] = [
    {
      headerName: 'Scope',
      field: 'ScopeType',
      maxWidth: 120,
      valueFormatter: params => this.faqScopeTypePipe.transform(params.value)
    },
    {
      headerName: 'Question',
      field: 'Question',
      flex: 2,
    },
    {
      headerName: 'Answer',
      field: 'Answer',
      flex: 2,
      valueFormatter: params => {
        const maxLength = 100;
        return params.value.length > maxLength
          ? params.value.substring(0, maxLength) + '...'
          : params.value;
      }
    },
    {
      headerName: 'Created On',
      field: 'CreatedOn',
      maxWidth: 140,
      valueFormatter: params => this.datePipe.transform(params.value, 'MMM d, y') ?? ''
    },
    {
      headerName: 'Active',
      field: 'Active',
      maxWidth: 80,
      cellRenderer: FormatBoolean,
    },
    {
      headerName: 'Actions',
      maxWidth: 180,
      cellRenderer: FaqManageActionsComponent,
      cellRendererParams: {
        onRefresh: () => this.refreshFaqs()
      }
    }
  ];

  ngOnInit(): void {
    if (this.canEditAdminAppFaqs) {
      this.selectedAppType.set(FaqScopeTypes.AdminApp);
    } else if (this.canEditWebappFaqs) {
      this.selectedAppType.set(FaqScopeTypes.WebApp);
    }

    this.frequentlyAskedQuestionsService.selectFaqs$().subscribe(faqs => {
      this.allFaqs.set(faqs);
    });
  }

  onGameChosen(game: IGameQueryResult): void {
    this.selectedGame.set(game);
    this.refreshFaqs();
  }

  onAppTypeChange(appType: FaqScopeTypes): void {
    this.selectedAppType.set(appType);
    this.refreshFaqs();
  }

  onFilterChange(filter: FaqFilterType): void {
    this.currentFilter.set(filter);
    if (filter === FaqFilterType.Shared) {
      this.refreshFaqs();
    }
  }

  refreshFaqs(): void {
    this.uiState = UIStateEnum.ShowLoading;
    const gameId = this.selectedGame()?.Id;

    this.frequentlyAskedQuestionsService.fetchAllFaqs(gameId).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  onFAQGridReady(params: GridReadyEvent<IFrequentlyAskedQuestions>): void {
    this.faqsGridApi = params.api;
    this.faqsGridApi.sizeColumnsToFit({defaultMinWidth: 90});
  }

  onAddFaqClick(): void {
    const dialogRef = this.matDialog.open(FaqAddComponent, {
      width: '700px',
      data: {
        canEditWebappFaqs: this.canEditWebappFaqs,
        canEditAdminAppFaqs: this.canEditAdminAppFaqs,
        selectedGameId: this.selectedGame()?.Id,
        selectedAppType: this.selectedAppType(),
        isShared: this.currentFilter() === FaqFilterType.Shared
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.refreshFaqs();
    });
  }
}
